import api from "./api";

/** @param {string} accessToken */
function userFromAccessToken(accessToken) {
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json);
    return {
      email: claims.sub,
      role: claims.role,
      universityId: claims.university_id ?? null,
      emailVerified: claims.email_verified,
    };
  } catch {
    return null;
  }
}

class AuthService {
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} API response
   */
  async login(email, password) {
    try {
      const response = await api.post("/users/login", {
        email,
        password,
      });

      const payload = response.data?.data;
      const accessToken = payload?.accessToken;

      if (response.data.success && accessToken) {
        localStorage.setItem("authToken", accessToken);

        if (payload.refreshToken) {
          localStorage.setItem("refreshToken", payload.refreshToken);
        } else {
          localStorage.removeItem("refreshToken");
        }

        if (typeof payload.expiresIn === "number" && payload.expiresIn > 0) {
          localStorage.setItem(
            "tokenExpiresAt",
            String(Date.now() + payload.expiresIn * 1000),
          );
        } else {
          localStorage.removeItem("tokenExpiresAt");
        }

        const fromToken = userFromAccessToken(accessToken);
        const user =
          payload.user ||
          fromToken ||
          (email ? { email } : null);
        if (user) {
          localStorage.setItem("user", JSON.stringify(user));
        }

        return { success: true, data: { ...payload, user } };
      }

      return {
        success: false,
        error: response.data.errorCode || "Login failed",
      };
    } catch (error) {
      // Error will be handled by API interceptor
      return {
        success: false,
        error:
          error.response?.data?.errorCode ||
          "Login failed. Please check your credentials.",
      };
    }
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("user");
    window.location.href = "/uni-admin/login";
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!localStorage.getItem("authToken");
  }

  /**
   * Get current user from localStorage
   * @returns {Object|null}
   */
  getCurrentUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  /**
   * Get auth token
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem("authToken");
  }
}

const authService = new AuthService();
export default authService;
